import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import {
  useAuthStore,
  useAccountDatabases,
  useCreateAccountDatabase,
  useUpdateAccountDatabase,
  useDeleteAccountDatabase,
  useTestAccountDatabase,
  useTestInlineConnection,
} from '@wordpuppi/shared';
import type {
  CreateDatabaseConnectionRequest,
  UpdateDatabaseConnectionRequest,
  DatabaseConnection,
} from '@wordpuppi/shared';
import { toast } from 'sonner';
import { confirmDialog } from '@/lib/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { LoadingSpinner } from '@/components/LoadingSkeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const Route = createFileRoute('/settings/databases')({
  component: DatabasesPage,
});

type DbType = 'postgres' | 'mysql' | 'sqlite';
type ConnectionMode = 'supabase' | 'connection_string' | 'connection_string_ssh';

function DatabasesPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const hasRedirected = useRef(false);
  const { data: databasesData, isLoading } = useAccountDatabases();
  const createDatabase = useCreateAccountDatabase();
  const updateDatabase = useUpdateAccountDatabase();
  const deleteDatabase = useDeleteAccountDatabase();
  const testDatabase = useTestAccountDatabase();
  const testInline = useTestInlineConnection();

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingDb, setEditingDb] = useState<DatabaseConnection | null>(null);
  const [connectionTested, setConnectionTested] = useState(false);
  const [newDbName, setNewDbName] = useState('');
  const [newDbType, setNewDbType] = useState<DbType>('postgres');
  const [connectionMode, setConnectionMode] = useState<ConnectionMode>('supabase');
  const [connectionString, setConnectionString] = useState('');
  const [projectRef, setProjectRef] = useState('');
  const [password, setPassword] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [usePooler, setUsePooler] = useState(true);

  // SSH state
  const [sshHost, setSshHost] = useState('');
  const [sshPort, setSshPort] = useState('22');
  const [sshUsername, setSshUsername] = useState('');
  const [sshPassword, setSshPassword] = useState('');

  // Helper to update field and reset test status
  const updateField = <T,>(setter: React.Dispatch<React.SetStateAction<T>>) => (value: T) => {
    setter(value);
    setConnectionTested(false);
  };

  useEffect(() => {
    if (!isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      navigate({ to: '/login' });
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  const databases = databasesData?.data ?? [];

  // Build preview URL for Supabase connection
  const supabasePreviewUrl =
    connectionMode === 'supabase' && projectRef
      ? `postgres://postgres.${projectRef}:****@${
          usePooler
            ? `aws-0-${region}.pooler.supabase.com:5432`
            : `db.${projectRef}.supabase.co:5432`
        }/postgres`
      : '';

  const isEditing = editingDb !== null;
  const isSaving = createDatabase.isPending || updateDatabase.isPending;

  const openAddModal = () => {
    resetForm();
    setEditingDb(null);
    setShowModal(true);
  };

  const openEditModal = (db: DatabaseConnection) => {
    if (db.db_type === "sqlite") return;
    setEditingDb(db);
    setNewDbName(db.name);
    setNewDbType(db.db_type);
    // Default to connection_string mode (or connection_string_ssh if ssh was enabled)
    setConnectionMode(db.ssh_enabled ? 'connection_string_ssh' : 'connection_string');
    setConnectionString('');
    setProjectRef('');
    setPassword('');
    setRegion('us-east-1');
    setUsePooler(true);
    setSshHost(db.ssh_host ?? '');
    setSshPort(String(db.ssh_port ?? 22));
    setSshUsername(db.ssh_username ?? '');
    setSshPassword('');
    setConnectionTested(false);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (newDbType === "sqlite") { toast.error("Use built-in SQLite on the hosting server instead of a saved file profile."); return; }
    if (!newDbName.trim()) {
      toast.error('Please enter a name for this connection');
      return;
    }

    if (isEditing) {
      // Build update payload — only include fields that changed or were filled
      const data: UpdateDatabaseConnectionRequest = {};
      if (newDbName.trim() !== editingDb!.name) data.name = newDbName.trim();
      if (newDbType !== editingDb!.db_type) data.db_type = newDbType;

      if (connectionMode === 'supabase') {
        if (projectRef) data.supabase_project_ref = projectRef;
        if (password) data.supabase_password = password;
        if (projectRef && password) data.supabase_region = region;
        if (projectRef && password) data.supabase_use_pooler = usePooler;
      } else if (connectionMode === 'connection_string_ssh') {
        if (connectionString) data.connection_string = connectionString;
        data.ssh_enabled = true;
        data.ssh_host = sshHost;
        data.ssh_port = parseInt(sshPort, 10) || 22;
        data.ssh_username = sshUsername;
        data.ssh_password = sshPassword;
      } else {
        if (connectionString) data.connection_string = connectionString;
        // If switching from SSH to non-SSH, explicitly disable
        if (editingDb!.ssh_enabled) data.ssh_enabled = false;
      }

      try {
        await updateDatabase.mutateAsync({ id: editingDb!.id, data });
        toast.success(`Database connection "${newDbName}" updated`);
        setShowModal(false);
        resetForm();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update connection');
      }
    } else {
      // Create flow
      const data: CreateDatabaseConnectionRequest = {
        name: newDbName.trim(),
        db_type: newDbType,
      };

      if (connectionMode === 'supabase') {
        data.supabase_project_ref = projectRef;
        data.supabase_password = password;
        data.supabase_region = region;
        data.supabase_use_pooler = usePooler;
      } else if (connectionMode === 'connection_string_ssh') {
        data.connection_string = connectionString;
        data.ssh_enabled = true;
        data.ssh_host = sshHost;
        data.ssh_port = parseInt(sshPort, 10) || 22;
        data.ssh_username = sshUsername;
        data.ssh_password = sshPassword;
      } else {
        data.connection_string = connectionString;
      }

      try {
        await createDatabase.mutateAsync(data);
        toast.success(`Database connection "${newDbName}" created`);
        setShowModal(false);
        resetForm();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create connection');
      }
    }
  };

  const resetForm = () => {
    setNewDbType("postgres");
    setEditingDb(null);
    setNewDbName('');
    setProjectRef('');
    setPassword('');
    setConnectionString('');
    setSshHost('');
    setSshPort('22');
    setSshUsername('');
    setSshPassword('');
    setConnectionTested(false);
  };

  // Build the request data for testing
  const buildTestData = (): CreateDatabaseConnectionRequest => {
    if (newDbType === "sqlite") throw new Error("Legacy SQLite profiles cannot be tested as new server connections.");
    const data: CreateDatabaseConnectionRequest = {
      name: newDbName.trim() || 'test',
      db_type: newDbType,
    };

    if (connectionMode === 'supabase') {
      data.supabase_project_ref = projectRef;
      data.supabase_password = password;
      data.supabase_region = region;
      data.supabase_use_pooler = usePooler;
    } else if (connectionMode === 'connection_string_ssh') {
      data.connection_string = connectionString;
      data.ssh_enabled = true;
      data.ssh_host = sshHost;
      data.ssh_port = parseInt(sshPort, 10) || 22;
      data.ssh_username = sshUsername;
      data.ssh_password = sshPassword;
    } else {
      data.connection_string = connectionString;
    }

    return data;
  };

  const handleTestInline = async () => {
    const data = buildTestData();

    try {
      const result = await testInline.mutateAsync(data);
      if (result.data?.healthy) {
        toast.success('Connection successful!');
        setConnectionTested(true);
      } else {
        toast.error(result.data?.message || 'Connection test failed');
        setConnectionTested(false);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Test failed');
      setConnectionTested(false);
    }
  };

  const handleDeleteDatabase = async (id: number, name: string) => {
    if (!(await confirmDialog(`Are you sure you want to delete "${name}"?`, { destructive: true, confirmLabel: 'Delete database' }))) {
      return;
    }

    try {
      await deleteDatabase.mutateAsync(id);
      toast.success('Database connection deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleTestDatabase = async (id: number) => {
    try {
      const result = await testDatabase.mutateAsync(id);
      if (result.data?.healthy) {
        toast.success('Connection successful!');
      } else {
        toast.error(result.data?.message || 'Connection test failed');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Test failed');
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-lg font-semibold">Databases</h1>
        <p className="text-sm text-muted-foreground">
          Manage the external databases available to your sites.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Database Connections</CardTitle>
              <CardDescription>
                External databases for storing site content
              </CardDescription>
            </div>
            <Button size="sm" onClick={openAddModal}>
              + Add Database
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner />
          ) : databases.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">
                No database connections yet
              </p>
              <Button variant="secondary" onClick={openAddModal}>
                Add Your First Database
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {databases.map((db) => (
                <div key={db.id} className="border rounded-lg p-3 space-y-2">
                  {db.db_type === "sqlite" ? <p className="text-sm text-muted-foreground">Legacy SQLite profile. Use built-in SQLite on the hosting server for sites and migrations. This saved record can be deleted; it is not a device database destination.</p> : null}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{db.name}</span>
                      <span className="text-xs text-muted-foreground uppercase shrink-0">
                        {db.db_type}
                      </span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={db.db_type === "sqlite"}
                        onClick={() => openEditModal(db)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTestDatabase(db.id)}
                        disabled={db.db_type === "sqlite" || testDatabase.isPending}
                      >
                        {testDatabase.isPending ? 'Testing...' : 'Test'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteDatabase(db.id, db.name)}
                        disabled={deleteDatabase.isPending}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  <div className="font-mono text-xs text-muted-foreground break-all">
                    {db.connection_preview}
                  </div>
                  {db.ssh_enabled && (
                    <div className="text-xs text-muted-foreground">
                      SSH: {db.ssh_username}@{db.ssh_host}
                      {db.ssh_port !== 22 && `:${db.ssh_port}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <Button variant="outline" onClick={() => navigate({ to: '/sites' })}>
          &larr; Back to Sites
        </Button>
      </div>

      {/* Add/Edit Database Modal */}
      <Dialog open={showModal} onOpenChange={(open) => !isSaving && setShowModal(open)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Database Connection' : 'Add Database Connection'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Connection Name</Label>
              <Input
                type="text"
                value={newDbName}
                onChange={(e) => setNewDbName(e.target.value)}
                placeholder="e.g., Production Supabase"
              />
            </div>

            <div className="space-y-2">
              <Label>Database Type</Label>
              <Select value={newDbType} onValueChange={(v: string) => updateField(setNewDbType)(v as DbType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="postgres">PostgreSQL</SelectItem>
                  <SelectItem value="mysql">MySQL</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Connection Mode</Label>
              <Select
                value={connectionMode}
                onValueChange={(v: string) => updateField(setConnectionMode)(v as ConnectionMode)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="supabase">Supabase</SelectItem>
                  <SelectItem value="connection_string">Connection String</SelectItem>
                  <SelectItem value="connection_string_ssh">Connection String + SSH</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {connectionMode === 'supabase' ? (
              <>
                <div className="space-y-2">
                  <Label>Project Reference</Label>
                  <Input
                    type="text"
                    value={projectRef}
                    onChange={(e) => updateField(setProjectRef)(e.target.value)}
                    placeholder="abcdefghijklmnop"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Database Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => updateField(setPassword)(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Region</Label>
                  <Select value={region} onValueChange={(v: string) => updateField(setRegion)(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="us-east-1">US East (N. Virginia)</SelectItem>
                      <SelectItem value="us-west-1">US West (N. California)</SelectItem>
                      <SelectItem value="eu-west-1">EU (Ireland)</SelectItem>
                      <SelectItem value="eu-central-1">EU (Frankfurt)</SelectItem>
                      <SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem>
                      <SelectItem value="ap-northeast-1">Asia Pacific (Tokyo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Pooling Mode</Label>
                  <Select
                    value={usePooler ? 'pooler' : 'direct'}
                    onValueChange={(v: string) => updateField(setUsePooler)(v === 'pooler')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pooler">Session Pooler (IPv4)</SelectItem>
                      <SelectItem value="direct">Direct (IPv6)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {supabasePreviewUrl && (
                  <div className="bg-muted p-3 rounded-md text-sm font-mono break-all">
                    {supabasePreviewUrl}
                  </div>
                )}
              </>
            ) : connectionMode === 'connection_string_ssh' ? (
              <>
                <div className="space-y-2">
                  <Label>Connection String</Label>
                  <Input
                    type="text"
                    value={connectionString}
                    onChange={(e) => updateField(setConnectionString)(e.target.value)}
                    placeholder={isEditing ? 'Leave blank to keep existing' : 'postgres://user:pass@host:5432/dbname'}
                  />
                </div>
                <div className="bg-muted p-3 rounded-md space-y-3">
                  <div className="font-medium text-sm">SSH Tunnel</div>
                  <div className="space-y-2">
                    <Label>SSH Host</Label>
                    <Input
                      type="text"
                      value={sshHost}
                      onChange={(e) => updateField(setSshHost)(e.target.value)}
                      placeholder="server.example.com"
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 space-y-2">
                      <Label>SSH Username</Label>
                      <Input
                        type="text"
                        value={sshUsername}
                        onChange={(e) => updateField(setSshUsername)(e.target.value)}
                        placeholder="root"
                      />
                    </div>
                    <div className="w-20 space-y-2">
                      <Label>Port</Label>
                      <Input
                        type="text"
                        value={sshPort}
                        onChange={(e) => updateField(setSshPort)(e.target.value)}
                        placeholder="22"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>SSH Password</Label>
                    <Input
                      type="password"
                      value={sshPassword}
                      onChange={(e) => updateField(setSshPassword)(e.target.value)}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Connection String</Label>
                <Input
                  type="text"
                  value={connectionString}
                  onChange={(e) => updateField(setConnectionString)(e.target.value)}
                  placeholder={isEditing ? 'Leave blank to keep existing' : 'postgres://user:pass@host:5432/dbname'}
                />
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                variant="secondary"
                onClick={handleTestInline}
                disabled={testInline.isPending || isSaving}
              >
                {testInline.isPending ? 'Testing...' : 'Test Connection'}
              </Button>
              <Button
                onClick={handleSave}
                disabled={!connectionTested || isSaving}
              >
                {isSaving ? 'Saving...' : isEditing ? 'Update Connection' : 'Save Connection'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowModal(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
